import type { I18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { creditNoteDeletedFilters } from './credit-note-deleted.filters';

// Manifesto de Operações excluídas (U23, U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K.
// A rota não tem permissão no legado (router.tsx:1311-1316); a consulta exige `CreditNote:Read`
// (creditContext.tsx:254-272) e restaurar, `CreditNote:Update` (DeletedCreditList.tsx:173).

export const CREDIT_NOTE_DELETED_PATH = '/ccb/excluidos';

export const CREDIT_NOTE_DELETED_READ = can('CreditNote', 'Read');
export const CREDIT_NOTE_DELETED_RESTORE = can('CreditNote', 'Update');

const mountedScreenRequired = (): never => {
  throw new Error(t`Abra a tela de operações excluídas para restaurar uma operação.`);
};

export const creditNoteDeletedListPage = listPageAction('creditNoteDeleted.list.page');

const filterKeys = creditNoteDeletedFilters.keys.join(', ');

// Descrições para o agente em pt-BR com `msg`, servidas no idioma do usuário (U46).
export const creditNoteDeletedFilter = filterAction('creditNoteDeleted.filter', creditNoteDeletedFilters, {
  description: msg`Filtra a lista de operações de crédito excluídas. Filtro omitido é limpo; datas são civis (AAAA-MM-DD); valores em reais (1.000,00). O fim do período de pagamento não é enviado, como no legado. Chaves: ${filterKeys}.`,
});

export const creditNoteDeletedRestoreInput = z.object({
  id: z.string().min(1).max(100),
  creditNoteNo: z.number().int().positive().optional(),
});
export type CreditNoteDeletedRestoreInput = z.infer<typeof creditNoteDeletedRestoreInput>;

/** Os textos do popup do legado (DeletedCreditListContainer.tsx:149-157), a mesma prévia do diálogo (U24). */
export function restorePreviewText(input: { readonly creditNoteNo?: number | undefined }) {
  const no = input.creditNoteNo;
  return {
    title: no === undefined ? t`Restaurar Operação` : t`Restaurar a operação #${no}?`,
    detail: t`Tem certeza que deseja restaurar essa operação?`,
  };
}

export const creditNoteDeletedRestore = action({
  id: 'creditNoteDeleted.restore',
  kind: 'write',
  row: true,
  label: msg`Restaurar operação`,
  description: msg`Restaura uma operação de crédito excluída dos resultados atuais, pelo id; ela volta para a lista de operações. Pede a confirmação da pessoa.`,
  keywords: [msg`restaurar`, msg`excluída`],
  permission: CREDIT_NOTE_DELETED_RESTORE,
  input: creditNoteDeletedRestoreInput,
  preview: (input) => restorePreviewText(input),
  // A tela troca este `run` pelo que usa a mutação e o feedback dela.
  run: mountedScreenRequired,
});

export const creditNoteDeletedScreen = defineScreen({
  id: 'creditNoteDeleted.list',
  path: CREDIT_NOTE_DELETED_PATH,
  title: msg`Excluídos`,
  description: msg`Operações de crédito excluídas do correspondente atual, com os filtros do legado (todos, status, data de início, data de pagamento, valor máximo e mínimo, número da operação) e paginação no servidor; dá para restaurar uma operação por vez.`,
  permission: CREDIT_NOTE_DELETED_READ,
  place: { icon: 'file-text', section: 'credit-note' },
  actions: [creditNoteDeletedListPage, creditNoteDeletedFilter, creditNoteDeletedRestore],
});

/** O que o agente lê da tela (U24), no idioma do usuário (U46): contagem e filtros ativos, sem dado pessoal nem de bureau. */
export function creditNoteDeletedContext(
  input: { readonly total: number | undefined; readonly filters: readonly string[] },
  i18n: I18n,
): ScreenContext {
  const total = input.total;
  const active = input.filters.join(', ');
  const state = total === undefined ? i18n._(msg`Carregando.`) : i18n._(msg`Operações excluídas encontradas: ${total}.`);
  const filters = input.filters.length > 0 ? i18n._(msg`Filtros: ${active}.`) : i18n._(msg`Sem filtros.`);
  return {
    summary: `${i18n._(msg`Operações de crédito excluídas.`)} ${state} ${filters}`,
    fields: { total: input.total ?? null },
    sensitivity: 'internal',
  };
}
